import { Css, Step, useComputed } from "@homebound/beam";
import { ObjectConfig, ObjectState, required, useFormState } from "@homebound/form-state";
import { StepperProvider, stepperBarHeight, useStepperContext } from "src/components/stepper";
import { useHiddenProjectSideNav } from "src/hooks";
import { useRouteParams } from "src/hooks/useRouteParams";
import { DefineChangeStep } from "src/routes/projects/change-events/guided/components/1.DefineChangeStep";
import { GuidedChangeEventStepActions } from "src/routes/projects/change-events/guided/components/GuidedChangeEventStepActions";
import { GuidedChangeEventStep, GuidedChangeType } from "src/routes/projects/change-events/guided/utils";
import { ProjectHeader } from "src/routes/projects/ProjectHeader";
import { ProjectParams } from "src/routes/routesDef";
import { createChangeEventsUrl } from "src/RouteUrls";

export function CreateGuidedChangeEventPage() {
  // Hide the project side nav for a full-screen wizard since this component renders inside `ProjectLayout`.
  useHiddenProjectSideNav();

  const formState = useFormState({ config: formConfig });

  const steps = useComputed<Step[]>(() => {
    const defineChangeComplete = !!formState.changeType.value && !!formState.reasonId.value;
    return [
      {
        label: "Describe Change",
        value: GuidedChangeEventStep.DefineChange,
        state: defineChangeComplete ? "complete" : "incomplete",
      },
      {
        label: "Select Option",
        value: GuidedChangeEventStep.SelectOption,
        state: "incomplete",
        disabled: !defineChangeComplete,
      },
      { label: "Review Option", value: GuidedChangeEventStep.ReviewOption, state: "incomplete", disabled: true },
      { label: "Review Impact", value: GuidedChangeEventStep.ReviewImpact, state: "incomplete", disabled: true },
      { label: "Review", value: GuidedChangeEventStep.Review, state: "incomplete", disabled: true },
    ];
  }, [formState]);

  return (
    <StepperProvider steps={steps}>
      <GuidedChangeEventWizard formState={formState} />
    </StepperProvider>
  );
}

type GuidedChangeEventFormInput = { changeType?: GuidedChangeType | null; reasonId?: string | null };
export type GuidedChangeEventFormState = ObjectState<GuidedChangeEventFormInput>;

const formConfig: ObjectConfig<GuidedChangeEventFormInput> = {
  changeType: { type: "value", rules: [required] },
  reasonId: { type: "value", rules: [required] },
};

type GuidedChangeEventWizardProps = { formState: GuidedChangeEventFormState };

function GuidedChangeEventWizard(props: GuidedChangeEventWizardProps) {
  const { formState } = props;
  const { projectId } = useRouteParams<ProjectParams>();
  const { currentStep } = useStepperContext();
  const isDefineChange = currentStep.value === GuidedChangeEventStep.DefineChange;

  return (
    <div css={Css.mbPx(stepperBarHeight).$}>
      <ProjectHeader projectId={projectId} title="Create Change Event" createUrl={createChangeEventsUrl} />
      <div css={Css.df.jcc.$}>
        {isDefineChange ? (
          <DefineChangeStep formState={formState} />
        ) : (
          <div css={Css.sm.gray700.$}>This step is not part of the prototype yet.</div>
        )}
      </div>
      <GuidedChangeEventStepActions formState={formState} projectId={projectId} />
    </div>
  );
}
